import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  postTutorProgressBatch,
  type TutorLessonProgressDTO,
} from '../api/tutor.client';
import { tutorProgressBatchQueryKey } from '../queryKeys';
import { normalizeLessonNumbers } from '../utils/normalizeLessonNumbers';
import { useTutorQueryIdentity } from './useTutorQueryIdentity';

export const TUTOR_PROGRESS_BATCH_STALE_MS = 30_000;

export type UseTutorProgressBatchOptions = {
  grade: number | null | undefined;
  lessonNumbers: readonly number[];
  enabled?: boolean;
};

export function useTutorProgressBatch(opts: UseTutorProgressBatchOptions) {
  const { membershipKey, studentKey } = useTutorQueryIdentity();
  const normalizedLessonNumbers = useMemo(
    () => normalizeLessonNumbers(opts.lessonNumbers),
    [opts.lessonNumbers],
  );
  const gradeReady =
    opts.grade != null && Number.isInteger(opts.grade) && opts.grade > 0;
  const enabled =
    (opts.enabled ?? true) && normalizedLessonNumbers.length > 0 && gradeReady;

  const queryKey = tutorProgressBatchQueryKey({
    membershipKey,
    studentKey,
    grade: opts.grade,
    lessonNumbers: normalizedLessonNumbers,
  });

  const query = useQuery<TutorLessonProgressDTO[], Error>({
    queryKey,
    queryFn: () => postTutorProgressBatch(normalizedLessonNumbers, opts.grade ?? undefined),
    enabled,
    staleTime: TUTOR_PROGRESS_BATCH_STALE_MS,
  });

  const progressByLesson = useMemo(() => {
    const map: Record<number, TutorLessonProgressDTO | undefined> = {};
    for (const row of query.data ?? []) {
      if (row?.lesson_no != null) map[row.lesson_no] = row;
    }
    return map;
  }, [query.data]);

  return {
    ...query,
    queryKey,
    normalizedLessonNumbers,
    progressByLesson,
    progressRows: query.data ?? [],
    isProgressLoading: enabled && query.isLoading,
  };
}
